import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'qrcode';
import { Copy, Check, Eye, EyeOff, ExternalLink, X, RefreshCw } from 'lucide-react';
import styles from './ConnectDisplayDialog.module.css';

/**
 * Connection is a discrete task, not an ongoing part of the workspace, so it
 * lives here rather than occupying permanent space in the editor.
 *
 * Priority is deliberate: QR is the primary mechanism (nobody types a long
 * URL into a TV), link is secondary, password tertiary.
 */

interface ConnectDisplayDialogProps {
  open: boolean;
  onClose: () => void;
  slideshowUrl: string | null;
  /** Short, typeable code (e.g. "K7RM-2PXQ") for entering directly on a TV. */
  displayCode: string | null;
  password: string | null;
  passwordLoading: boolean;
  passwordUnavailable: boolean;
  onChangePassword: () => void;
}

export function ConnectDisplayDialog({
  open,
  onClose,
  slideshowUrl,
  displayCode,
  password,
  passwordLoading,
  passwordUnavailable,
  onChangePassword,
}: ConnectDisplayDialogProps) {
  const [qr, setQr] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [qrNonce, setQrNonce] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open || !slideshowUrl) return;
    let cancelled = false;
    QRCode.toDataURL(slideshowUrl, { width: 420, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (cancelled) return;
        setQr(url);
        setQrFailed(false);
      })
      .catch(() => !cancelled && setQrFailed(true));
    return () => {
      cancelled = true;
    };
  }, [open, slideshowUrl, qrNonce]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const copy = useCallback(async (text: string, mark: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      mark(true);
      setTimeout(() => mark(false), 2000);
    } catch {
      /* Clipboard denied — the value is selectable as a fallback. */
    }
  }, []);

  // Host only — the part a person actually has to type.
  const typeHost = slideshowUrl ? new URL(slideshowUrl).host : '';

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles['backdrop']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-title"
            className={styles['dialog']}
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          >
            <header className={styles['head']}>
              <h2 id="connect-title" className={styles['title']}>Connect your TV</h2>
              <button type="button" onClick={onClose} className={styles['close']} aria-label="Close">
                <X size={16} />
              </button>
            </header>

            <div className={styles['body']}>
              <p className={styles['lede']}>Open this display on your TV</p>

              <div className={styles['qr-wrap']}>
                {qrFailed ? (
                  <div className={styles['qr-error']}>
                    <p className={styles['muted']}>Couldn&rsquo;t generate QR code.</p>
                    <button type="button" className={styles['link-btn']} onClick={() => setQrNonce((n) => n + 1)}>
                      <RefreshCw size={13} /> Retry
                    </button>
                  </div>
                ) : qr ? (
                  <img src={qr} alt="QR code for the display link" className={styles['qr']} />
                ) : (
                  <div className={styles['qr-skeleton']} />
                )}
              </div>

              <p className={styles['hint']}>
                Scan this QR code with your phone, then open the display on your TV.
              </p>

              <div className={styles['rule']} />

              {/* The path for someone standing at the TV with a remote: a
                  short host plus a code, rather than a 60-character URL. */}
              {displayCode && (
                <div className={styles['field']}>
                  <span className={styles['label']}>Or type it on your TV</span>
                  <p className={styles['type-host']}>{typeHost}/tv</p>
                  <div className={styles['code-row']}>
                    <span className={styles['code']}>{displayCode}</span>
                    <button
                      type="button"
                      className={styles['inline-action']}
                      onClick={() => copy(displayCode, setCodeCopied)}
                      aria-label="Copy display code"
                    >
                      {codeCopied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              )}

              <div className={styles['field']}>
                <label className={styles['label']} htmlFor="connect-link">Display link</label>
                <div className={styles['input-row']}>
                  <span id="connect-link" className={styles['value']} title={slideshowUrl ?? ''}>
                    {slideshowUrl}
                  </span>
                  <button
                    type="button"
                    className={styles['inline-action']}
                    onClick={() => slideshowUrl && copy(slideshowUrl, setLinkCopied)}
                  >
                    {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                    {linkCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className={styles['field']}>
                <span className={styles['label']}>Password</span>
                <div className={styles['input-row']}>
                  {passwordLoading ? (
                    <span className={styles['muted']}>Loading…</span>
                  ) : passwordUnavailable ? (
                    <>
                      <span className={styles['muted']}>Not available for this display</span>
                      <button type="button" className={styles['inline-action']} onClick={onChangePassword}>
                        Set new
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={styles['value']}>
                        {showPassword ? password : '•'.repeat(password?.length ?? 8)}
                      </span>
                      <button
                        type="button"
                        className={styles['inline-action']}
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                      <button
                        type="button"
                        className={styles['inline-action']}
                        onClick={() => password && copy(password, setPasswordCopied)}
                        aria-label="Copy password"
                      >
                        {passwordCopied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={styles['rule']} />

              <div className={styles['field']}>
                <span className={styles['label']}>Open on this device</span>
                {slideshowUrl && (
                  <a
                    href={slideshowUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles['open-btn']}
                  >
                    Open Display
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
